// src/pages/public/LocationSearch.tsx
import { useState } from "react";
import LocationSearchComponent from "@/features/scheduling/components/LocationSearch";
import { useLazySearchClinicsQuery } from "@/api/clinicApi";
import type { VendorLocation } from "@/features/scheduling/types";

export default function LocationSearch() {
  const [vendors, setVendors] = useState<VendorLocation[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorLocation | null>(null);
  const [defaultZip, setDefaultZip] = useState("");

  const [triggerSearch, { isFetching }] = useLazySearchClinicsQuery();

  const onSearch = async (zipCode: string, radius: number) => {
    setDefaultZip(zipCode);
    try {
      const res = await triggerSearch({ zipCode, radius });
      // RTK Query lazy trigger returns an object that may include `data`
      const data: any = (res as any)?.data ?? (res as any);
      if (!data || !Array.isArray(data)) {
        setVendors([]);
        return;
      }
      // Map backend Clinic -> VendorLocation shape expected by the map component
      const mapped: VendorLocation[] = data.map((c: any) => ({
        id: c.id,
        vendorId: c.vendorId ?? 0,
        vendorName: (c.vendorName as string) || c.clinicName || "Clinic",
        clinicName: c.clinicName || c.vendorName || "",
        address: c.clinicAddress || "",
        city: c.city || "",
        state: c.state || "",
        zipCode: c.zipCode || "",
        phone: c.contactPhone || c.phone || "",
        distance: 0,
        latitude: c.latitude,
        longitude: c.longitude,
        appointmentRequired: !!c.appointmentRequired,
        cocRequired: !!c.cocRequired,
        operatingHours: c.operatingHours,
        price: c.price,
        collectionFee: c.collectionFee,
      }));
      setVendors(mapped);
    } catch (err) {
      setVendors([]);
      console.error("Search clinics failed", err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Find Testing Locations</h1>
        <p className="text-gray-600">Search for nearby clinics by ZIP code</p>
      </div>

      <LocationSearchComponent
        vendors={vendors}
        isLoading={isFetching}
        selectedVendor={selectedVendor}
        onSelectVendor={(v: VendorLocation | null) => setSelectedVendor(v)}
        onSearch={onSearch}
        defaultZipCode={defaultZip}
      />
    </div>
  );
}
